// src/components/AllNotifications.js

import React, { useEffect, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  fetchNotifications,
  markNotificationAsRead,
  markAllNotificationAsRead,
} from "../actions/notificationActions";
import { LazyLoading } from "./ui/LazyLoading";
import {
  Bell,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  Info,
  CheckCheck,
} from "lucide-react";

const AllNotifications = () => {
  const dispatch = useDispatch();
  const {
    items: notifications = [],
    loading,
    error,
  } = useSelector((state) => state.notifications || {});

  // Fetch all notifications on mount
  useEffect(() => {
    dispatch(fetchNotifications());
  }, [dispatch]);

  const handleMarkAllAsRead = () => {
    dispatch(markAllNotificationAsRead());
  };

  const handleMarkAsRead = (id) => {
    dispatch(markNotificationAsRead(id));
  };

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.is_read).length,
    [notifications]
  );

  const getTypeStyles = (type) => {
    switch (type) {
      case "success":
        return {
          iconBg: "bg-emerald-100 text-emerald-600",
          pillBg: "bg-emerald-50 text-emerald-700",
          pillText: "موفق",
          border: "border-emerald-100 bg-emerald-50/60",
        };
      case "warning":
        return {
          iconBg: "bg-amber-100 text-amber-600",
          pillBg: "bg-amber-50 text-amber-700",
          pillText: "هشدار",
          border: "border-amber-100 bg-amber-50/60",
        };
      case "error":
        return {
          iconBg: "bg-rose-100 text-rose-600",
          pillBg: "bg-rose-50 text-rose-700",
          pillText: "خطا",
          border: "border-rose-100 bg-rose-50/60",
        };
      default:
        return {
          iconBg: "bg-sky-100 text-sky-600",
          pillBg: "bg-sky-50 text-sky-700",
          pillText: "اطلاع",
          border: "border-sky-100 bg-sky-50/60",
        };
    }
  };

  if (loading) {
    return <LazyLoading />;
  }

  if (error) {
    return (
      <div className="mt-24 min-h-[40vh] flex items-center justify-center px-4 font-vazir">
        <div className="max-w-lg w-full rounded-2xl border border-rose-200 bg-rose-50 px-4 py-5 text-rose-700 text-right">
          <div className="flex flex-row-reverse items-center gap-2 mb-2">
            <XCircle className="w-5 h-5" />
            <span className="font-semibold">خطا در دریافت اعلان‌ها</span>
          </div>
          <p className="text-sm leading-relaxed">{String(error)}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-24 pb-16 bg-gray-50 min-h-screen font-vazir">
      <div className="max-w-4xl mx-auto px-3 sm:px-4 md:px-6">
        {/* Header card */}
        <div className="rounded-2xl bg-white/80 backdrop-blur-md border border-white/60 shadow-sm px-4 sm:px-6 py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-xs">
                مجموع اعلان‌ها:{" "}
                <span className="font-semibold ml-1">
                  {notifications.length}
                </span>
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-indigo-50 text-indigo-700 text-xs">
                خوانده نشده:{" "}
                <span className="font-semibold ml-1">{unreadCount}</span>
              </span>
            </div>

            <button
              type="button"
              onClick={handleMarkAllAsRead}
              disabled={!unreadCount}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs sm:text-sm
                         border border-slate-200 bg-slate-50 text-slate-700
                         hover:bg-slate-100 hover:border-slate-300
                         disabled:bg-slate-100 disabled:text-slate-400 disabled:cursor-not-allowed
                         transition-colors"
            >
              <CheckCheck className="w-4 h-4" />
              خواندن همه
            </button>
          </div>
          <div className="flex flex-row-reverse items-center gap-3 text-right">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-indigo-500 to-sky-500 flex items-center justify-center text-white shadow-sm">
              <Bell className="w-5 h-5" />
            </div>
            <div className="flex flex-col items-end">
              <h1 className="text-xl sm:text-2xl font-bold text-slate-900">
                تمام اعلان‌ها
              </h1>
              <p className="text-xs sm:text-sm text-slate-500">
                مشاهده و مدیریت اعلان‌های سیستم اوژن تجارت کیان
              </p>
            </div>
          </div>
        </div>

        {/* Notifications list */}
        <div className="mt-4 rounded-2xl bg-white/80 backdrop-blur-md border border-white/60 shadow-sm px-3 sm:px-5 py-4">
          {notifications.length === 0 ? (
            <div className="py-10 text-center text-slate-500 text-sm">
              هیچ اعلانی یافت نشد.
            </div>
          ) : (
            <div className="max-h-[70vh] overflow-y-auto pr-1 space-y-3">
              {notifications.map((notif) => {
                const typeStyles = getTypeStyles(notif.type);

                return (
                  <div
                    key={notif.id}
                    className={`flex flex-row-reverse items-center gap-3 rounded-2xl border px-3.5 py-3 sm:px-4 sm:py-3.5 text-right
                                ${typeStyles.border} ${
                      notif.is_read ? "opacity-80" : ""
                    }`}
                  >
                    {/* Icon column */}

                    {/* Content */}
                    <div className="flex-1 flex flex-col items-end gap-1">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between w-full gap-2"></div>

                      <p className="text-xs sm:text-sm text-slate-800 leading-relaxed">
                        {notif.message}
                      </p>

                      {notif.created_at && (
                        <span className="text-[0.7rem] text-slate-400 mt-1">
                          {/* If backend already returns a human-readable or ISO date; adjust as needed */}
                          {notif.created_at}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1 sm:gap-2 ">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[0.7rem] font-medium ${typeStyles.pillBg}`}
                      >
                        {typeStyles.pillText}
                      </span>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[0.7rem] font-medium ${
                          notif.is_read
                            ? "bg-slate-100 text-slate-600"
                            : "bg-indigo-50 text-indigo-700"
                        }`}
                      >
                        {notif.is_read ? "خوانده شده" : "خوانده نشده"}
                      </span>
                    </div>

                    {!notif.is_read && (
                      <button
                        type="button"
                        onClick={() => handleMarkAsRead(notif.id)}
                        className="self-center sm:self-auto inline-flex items-center gap-1.5 px-2.5 py-1 
                                       rounded-xl text-[0.7rem] border border-indigo-200 
                                       bg-indigo-50 text-indigo-700 hover:bg-indigo-100 
                                       transition-colors"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        خوانده شد
                      </button>
                    )}
                    <div
                      className={`mt-0.5 w-9 h-9 flex items-center justify-center rounded-xl shadow-sm ${typeStyles.iconBg}`}
                    >
                      {notif.type === "success" && (
                        <CheckCircle2 className="w-5 h-5" />
                      )}
                      {notif.type === "warning" && (
                        <AlertTriangle className="w-5 h-5" />
                      )}
                      {notif.type === "error" && (
                        <XCircle className="w-5 h-5" />
                      )}
                      {notif.type === "info" && <Info className="w-5 h-5" />}
                      {!["success", "warning", "error", "info"].includes(
                        notif.type
                      ) && <Bell className="w-5 h-5" />}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AllNotifications;
